import { useParams } from '@jupiter/plugin-runtime/router';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  DropdownDivider,
  Toast,
  Typography,
  Space,
  Empty,
  Spin,
} from '@douyinfe/semi-ui';
import { SylApi } from '@syllepsis/adapter';
import { IllustrationNoResult } from '@douyinfe/semi-illustrations';
import dayjs from 'dayjs';
import { IconEdit, IconThumbUpStroked } from '@douyinfe/semi-icons';
import { Element, scroller } from 'react-scroll';
import { LoginMask } from '../LoginMask';
import { IconPost } from '../IconPost';
import PostHeader from './PostHeader';
import useBiz from '@/context/useBiz';
import EditorV2 from '@/components/EditorV2';
import { CommentList } from '@/components/CommentList';
import { useComment as useCommentApi } from '@/hooks/useComment';
import useComment from '@/context/useComment';
import { SafeHtmlContent } from '@/components/Safe';
import { usePost } from '@/hooks/usePost';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useLike } from '@/hooks/useLike';
import { reportCreateComment, reportViewPost } from '@/report';
import usePostImageSet from '@/hooks/usePostImageSet';

const metaToHtml = (meta: Record<string, string>, canSee = false) => {
  let html = '';
  const specialAttr = ['levelOne', 'levelTwo', 'levelThree'];
  const map = {
    levelOne: '发帖模块',
    levelTwo: '问题类型',
    levelThree: '问题模块',
  };

  specialAttr.forEach(attr => {
    if (typeof meta[attr] === 'string') {
      html += `<p>${map[attr as keyof typeof map]}: ${meta[attr]}</p>`;
    }
  });
  if (canSee) {
    html += '<hr />';
    html += '<p>详细信息:</p>';
    html += `<ul>${Object.keys(meta)
      .filter(key => !specialAttr.includes(key))
      .map(
        subKey =>
          `<li>${subKey === 'Title' ? '标题' : subKey}: ${meta[subKey]}</li>`,
      )
      .join('')}</ul>`;
  }
  return (
    <SafeHtmlContent>{`<blockquote>${html}</blockquote>`}</SafeHtmlContent>
  );
};

const Wrapper: React.FC = ({ children }) => (
  <div className="bg-white relative rounded-xl my-6 mx-auto px-8 py-6 h-full w-full max-w-[1200px]">
    {children}
  </div>
);

export const PostDetail: React.FC = () => {
  const { postId } = useParams<{
    postId?: string;
  }>();
  const biz = useBiz(state => state.biz);
  const editorRef = useRef<SylApi | null>(null);
  const { toggleLike, togglingLike } = useLike();
  const { postDetail, fetchPost, fetchingPost } = usePost();
  const [likeCount, setLikeCount] = useState(0);
  const [isLike, setIsLike] = useState(false);
  const { ifLogin, ifAdmin, User: currentUser, loading } = useUserInfo();

  const toLogin = () => {
    window.Garfish.channel.emit('main:login-activate');
  };
  const { replaceUrl, getExitUris } = usePostImageSet();

  const { run: postComment, createLoading } = useCommentApi();

  const reFetchComment = useComment(state => state.reFetchComment);

  useEffect(() => {
    // 前端兜底逻辑
    if (postDetail?.PostInfo?.IsLiked && !postDetail?.PostInfo?.Liked) {
      setLikeCount(1);
    } else {
      setLikeCount(postDetail?.PostInfo?.Liked ?? 0);
    }
    setIsLike(Boolean(postDetail?.PostInfo?.IsLiked));
  }, [postDetail?.PostInfo?.Liked, postDetail?.PostInfo?.IsLiked]);

  useEffect(() => {
    if (!loading) {
      reportViewPost({
        post_id: postId,
        uid: currentUser?.Id || '',
        post_type: postDetail?.PostInfo.Type,
      });
    }
  }, [loading, postId, currentUser?.Id]);

  if (fetchingPost) {
    return (
      <Wrapper>
        <div className="flex justify-center items-center h-[400px]">
          <Spin />
        </div>
      </Wrapper>
    );
  }

  if (!postDetail?.PostInfo) {
    return (
      <Empty
        image={<IllustrationNoResult style={{ width: 150, height: 150 }} />}
        description={'帖子不存在或已删除'}
        className="w-[1200px] h-[520px] bg-white rounded-xl pt-[138px] mx-auto mt-8"
      />
    );
  }

  return (
    <Wrapper>
      <IconPost />
      <PostHeader
        info={postDetail?.PostInfo}
        refetch={() => fetchPost({ Biz: biz, PostId: postId })}
      />
      {Object.keys(postDetail?.PostInfo?.Meta ?? {}).length > 0 &&
        postDetail?.PostInfo?.Type === 'question' &&
        metaToHtml(postDetail?.PostInfo?.Meta ?? {}, Boolean(ifAdmin))}

      <SafeHtmlContent>{postDetail?.PostInfo?.Html}</SafeHtmlContent>
      <Typography.Text type="tertiary" className="mt-6">
        最后一次编辑于{' '}
        {dayjs(postDetail?.PostInfo?.UpdatedAt).format('YYYY 年 MM 月 DD 日')}
      </Typography.Text>
      <div className="space-x-4 mt-8">
        <Button
          type="tertiary"
          className={isLike ? 'bg-[#F3F8FF] text-semi-color-primary' : ''}
          loading={togglingLike}
          onClick={async () => {
            if (!ifLogin) {
              toLogin();
              return;
            }
            await toggleLike({
              Uid: '',
              Type: 'post',
              TypeId: postId,
              Biz: biz,
            });
            if (isLike) {
              setLikeCount(n => n - 1);
              setIsLike(false);
            } else {
              setLikeCount(n => n + 1);
              setIsLike(true);
            }
          }}>
          <Space align="center">
            <IconThumbUpStroked className="mr-2" />
            {isLike && '已'}点赞 {likeCount === 0 ? '' : likeCount}
          </Space>
        </Button>

        <Button
          theme="solid"
          onClick={() => {
            scroller.scrollTo('reply-editor', {
              duration: 1500,
              smooth: true,
            });
          }}>
          <Space align="center">
            <IconEdit />
            回复
          </Space>
        </Button>
      </div>
      <DropdownDivider className="my-8" />
      <Typography.Title heading={6}>
        共 {postDetail?.PostInfo?.CommentCount} 条评论
      </Typography.Title>
      <CommentList postId={postDetail?.PostInfo.Id || ''} />
      <Element name="reply-editor">
        <div className="py-4">
          <LoginMask>
            <EditorV2
              getEditorInstance={editor => {
                editorRef.current = editor;
              }}
            />

            <div className="flex items-center justify-center">
              <Button
                loading={createLoading}
                type="primary"
                theme={'solid'}
                className={' center mt-4  '}
                onClick={async () => {
                  const html = editorRef.current?.getHTML() || '';
                  if (!html) {
                    return Toast.error('请输入内容');
                  }

                  const postContent = replaceUrl(html);
                  const res = await postComment({
                    Html: postContent,
                    Uid: '',
                    Biz: biz,
                    PostId: postId ?? '',
                    Uris: getExitUris(postContent),
                  });
                  editorRef.current?.setHTML('');
                  reportCreateComment({
                    post_id: postId,
                    comment_id: res.CommentId,
                    uid: currentUser?.Id || '',
                    to_uid: postDetail.PostInfo.UserId,
                  });

                  return reFetchComment?.();
                }}>
                提交
              </Button>
            </div>
          </LoginMask>
        </div>
      </Element>
    </Wrapper>
  );
};
